// src/components/settings/ServiceTypesTab.jsx
// v2: Added Phase 1 Industry-Friendly Pricing
// - Reverse calculator: "What do you charge for typical X?" → auto-calculate rate
// - Friendly labels editor (Small/Medium/Large Yard)
// - Industry template defaults
// - Preview panel showing what customers see
import React, { useState, useEffect, useRef } from 'react';
import { 
  Plus, Edit2, Trash2, Briefcase, X, Calculator, 
  DollarSign, ChevronDown, ChevronUp, HelpCircle, Check,
  Percent, ToggleLeft, ToggleRight, Eye, Lightbulb, Sparkles,
  Settings2
} from 'lucide-react';
import api from '../../services/api';
import { industryService } from '../../services/industryService';
import ConfirmModal from '../ConfirmModal';
import { 
  getIndustryTemplate, 
  calculateRateFromTypical,
  formatRate 
} from '../../config/industryTemplates';

// Pricing methods with clear descriptions
const PRICING_METHODS = {
  FLAT_RATE: {
    id: 'flat-rate',
    name: 'Flat Rate',
    description: 'Same price every time',
    example: 'e.g., Gutter Cleaning - $150',
    unit: 'Job',
  },
  PER_SQFT: {
    id: 'per-sqft',
    name: 'Per Square Foot',
    description: 'Price based on area',
    example: 'e.g., Driveway - $0.15/sq ft',
    unit: 'Sq Ft',
  },
  PER_LINEAR_FT: {
    id: 'per-linear-ft',
    name: 'Per Linear Foot',
    description: 'Price based on length',
    example: 'e.g., Fence - $2.00/linear ft',
    unit: 'Linear Ft',
  },
  PER_HOUR: {
    id: 'per-hour',
    name: 'Per Hour',
    description: 'Price based on time',
    example: 'e.g., Handyman - $75/hour',
    unit: 'Hour',
  },
  PER_UNIT: {
    id: 'per-unit',
    name: 'Per Unit/Item',
    description: 'Price per individual item',
    example: 'e.g., Window Cleaning - $8/window',
    unit: 'Unit',
  },
  TIERED: {
    id: 'tiered',
    name: 'Tiered Pricing',
    description: 'Different prices by size/level',
    example: 'e.g., Small $99, Medium $149, Large $199',
    unit: 'Job',
  },
};

// Default suggestions - shown when user has no existing categories
const DEFAULT_CATEGORY_SUGGESTIONS = [
  'Residential',
  'Commercial', 
  'Maintenance',
  'Repair',
  'Installation',
  'Emergency',
  'Inspection',
  'Consultation',
  'Specialty',
  'Add-Ons',
  'Other'
];

const ServiceTypesTab = () => {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Category autocomplete state
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [existingCategories, setExistingCategories] = useState([]);
  const categoryInputRef = useRef(null);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, service: null });

  // Business industry (fetched from API)
  const [businessIndustry, setBusinessIndustry] = useState('Other');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    isActive: true,
    // Pricing
    pricingMethod: 'flat-rate',
    price: '',
    baseRate: '',
    minimumCharge: '',
    tieredRates: [],
    // Modifiers / Add-ons
    modifiers: [],
    // NEW: Friendly Labels (Phase 1)
    friendlyLabels: [],
    useFriendlyLabelsOnPublicForm: false,
    setupPrompt: '',
    typicalMeasurement: '',
    // NEW: Reverse calculator
    typicalPrice: '', // User enters this, we calculate rate
  });

  // UI state
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showModifiers, setShowModifiers] = useState(false);
  const [showFriendlyLabels, setShowFriendlyLabels] = useState(false);
  const [useSimpleMode, setUseSimpleMode] = useState(true); // Simple vs Advanced rate entry
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    loadServiceTypes();
    loadBusinessIndustry();
  }, []);

  // Extract unique categories from existing service types
  useEffect(() => {
    const categories = [...new Set(
      serviceTypes
        .map(s => s.category)
        .filter(Boolean)
    )].sort();
    setExistingCategories(categories);
  }, [serviceTypes]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryInputRef.current && !categoryInputRef.current.contains(e.target)) {
        setShowCategorySuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Auto-calculate rate when typical price changes (reverse calculator)
  useEffect(() => {
    if (useSimpleMode && formData.typicalPrice && formData.typicalMeasurement) {
      const rate = calculateRateFromTypical(
        parseFloat(formData.typicalPrice),
        parseFloat(formData.typicalMeasurement)
      );
      if (rate !== null) {
        setFormData(prev => ({ ...prev, baseRate: rate.toString() }));
      }
    }
  }, [formData.typicalPrice, formData.typicalMeasurement, useSimpleMode]);

  const loadServiceTypes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/servicetypes');
      setServiceTypes(response.data || []);
    } catch (err) {
      console.error('Error loading service types:', err);
      setError('Failed to load service types');
    } finally {
      setLoading(false);
    }
  };

  const loadBusinessIndustry = async () => {
    try {
      const theme = await industryService.getMyTheme();
      if (theme?.serviceType) {
        setBusinessIndustry(theme.serviceType);
      }
    } catch (err) {
      console.error('Error loading business industry:', err);
      // Default to 'Other' on error (already set as initial state)
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      isActive: true,
      pricingMethod: 'flat-rate',
      price: '',
      baseRate: '',
      minimumCharge: '',
      tieredRates: [],
      modifiers: [],
      friendlyLabels: [],
      useFriendlyLabelsOnPublicForm: false,
      setupPrompt: '',
      typicalMeasurement: '',
      typicalPrice: '',
    });
    setShowAdvanced(false);
    setShowModifiers(false);
    setShowFriendlyLabels(false);
    setUseSimpleMode(true);
    setShowPreview(false);
    setEditingService(null);
    setError(null);
  };

  // Parse modifiers from JSON string
  const parseModifiers = (modifiersJson) => {
    if (!modifiersJson) return [];
    try {
      return JSON.parse(modifiersJson);
    } catch {
      return [];
    }
  };

  // Parse friendly labels from JSON string
  const parseFriendlyLabels = (friendlyLabelsJson) => {
    if (!friendlyLabelsJson) return [];
    try {
      return JSON.parse(friendlyLabelsJson);
    } catch {
      return [];
    }
  };

  // Load industry defaults when creating new service
  const loadIndustryDefaults = () => {
    if (!businessIndustry) return;
    
    const template = getIndustryTemplate(businessIndustry);
    if (!template) return;

    setFormData(prev => ({
      ...prev,
      pricingMethod: template.defaultMethod || 'flat-rate',
      friendlyLabels: template.friendlyLabels || [],
      setupPrompt: template.setupPrompt || '',
      typicalMeasurement: template.typicalEstimate?.toString() || '',
      useFriendlyLabelsOnPublicForm: !!template.friendlyLabels,
    }));

    if (template.friendlyLabels) {
      setShowFriendlyLabels(true);
    }
  };

  const handleOpenModal = (service = null) => {
    if (service) {
      setEditingService(service);
      
      // Determine pricing method from existing data
      let pricingMethod = 'flat-rate';
      if (service.pricingTemplate) {
        pricingMethod = service.pricingTemplate;
      }
      
      // Parse modifiers and friendly labels
      const modifiers = parseModifiers(service.modifiersJson);
      const friendlyLabels = parseFriendlyLabels(service.friendlyLabelsJson);
      
      setFormData({
        name: service.name || '',
        description: service.description || '',
        category: service.category || '',
        isActive: service.isActive ?? true,
        pricingMethod: pricingMethod,
        price: service.defaultPrice?.toString() || '',
        baseRate: service.baseRate?.toString() || '',
        minimumCharge: service.minimumCharge?.toString() || '',
        tieredRates: service.tieredRates || [],
        modifiers: modifiers,
        friendlyLabels: friendlyLabels,
        useFriendlyLabelsOnPublicForm: service.useFriendlyLabelsOnPublicForm || false,
        setupPrompt: service.setupPrompt || '',
        typicalMeasurement: service.typicalMeasurement?.toString() || '',
        // Back-calculate typical price from rate × typicalMeasurement
        typicalPrice: (service.baseRate && service.typicalMeasurement) 
          ? (service.baseRate * service.typicalMeasurement).toFixed(2)
          : '',
      });
      
      // Show advanced if there's a minimum charge or tiers
      setShowAdvanced(!!service.minimumCharge || (service.tieredRates && service.tieredRates.length > 0));
      // Show modifiers if there are any
      setShowModifiers(modifiers.length > 0);
      // Show friendly labels if there are any
      setShowFriendlyLabels(friendlyLabels.length > 0);
      // Use Simple Mode if we have typicalMeasurement (Easy Setup was used)
      // Otherwise use Advanced mode (direct rate entry)
      setUseSimpleMode(!!service.typicalMeasurement);
    } else {
      resetForm();
      // Load industry defaults for new services
      loadIndustryDefaults();
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handlePricingMethodChange = (methodId) => {
    setFormData(prev => ({
      ...prev,
      pricingMethod: methodId,
      // Clear pricing fields when switching
      price: methodId === 'flat-rate' ? prev.price : '',
      baseRate: methodId !== 'flat-rate' ? prev.baseRate : '',
      tieredRates: methodId === 'tiered' ? prev.tieredRates : [],
      typicalPrice: '',
    }));

    // Load industry-specific friendly labels for the new method
    if (businessIndustry && methodId !== 'flat-rate' && methodId !== 'tiered') {
      const template = getIndustryTemplate(businessIndustry);
      if (template && template.defaultMethod !== methodId) {
        // Template values are for a different unit (e.g. sq ft house sizes on a linear ft service)
        setFormData(prev => ({ ...prev, friendlyLabels: [], setupPrompt: '', typicalMeasurement: '' }));
        setShowFriendlyLabels(false);
        setUseSimpleMode(false);
      } else if (template && template.friendlyLabels) {
        setFormData(prev => ({
          ...prev,
          friendlyLabels: template.friendlyLabels,
          setupPrompt: template.setupPrompt || '',
          typicalMeasurement: template.typicalEstimate?.toString() || '',
        }));
        setShowFriendlyLabels(true);
      }
    }
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleCategorySelect = (category) => {
    setFormData(prev => ({ ...prev, category }));
    setShowCategorySuggestions(false);
  };

  // Get filtered category suggestions
  const getCategorySuggestions = () => {
    const input = formData.category.toLowerCase().trim();
    const allCategories = [...new Set([...existingCategories, ...DEFAULT_CATEGORY_SUGGESTIONS])];
    
    if (!input) {
      return allCategories;
    }
    
    return allCategories.filter(cat => 
      cat.toLowerCase().includes(input)
    );
  };

  // Get the unit based on pricing method
  const getUnitForMethod = (methodId) => {
    const method = Object.values(PRICING_METHODS).find(m => m.id === methodId);
    return method?.unit || 'Job';
  };

  // =====================================================
  // MODIFIER HELPERS
  // =====================================================
  
  const addModifier = () => {
    setFormData(prev => ({
      ...prev,
      modifiers: [
        ...prev.modifiers,
        { name: '', description: '', type: 'flat', amount: '', enabled: true }
      ]
    }));
    setShowModifiers(true);
  };

  const updateModifier = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod, i) =>
        i === index ? { ...mod, [field]: value } : mod
      )
    }));
  };

  const removeModifier = (index) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.filter((_, i) => i !== index)
    }));
  };

  const toggleModifierEnabled = (index) => {
    setFormData(prev => ({
      ...prev,
      modifiers: prev.modifiers.map((mod, i) =>
        i === index ? { ...mod, enabled: !mod.enabled } : mod
      )
    }));
  };

  // =====================================================
  // FRIENDLY LABELS HELPERS
  // =====================================================

  const addFriendlyLabel = () => {
    setFormData(prev => ({
      ...prev,
      friendlyLabels: [
        ...prev.friendlyLabels,
        { label: '', estimate: '', description: '' }
      ]
    }));
    setShowFriendlyLabels(true);
  };

  const updateFriendlyLabel = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      friendlyLabels: prev.friendlyLabels.map((label, i) =>
        i === index ? { ...label, [field]: value } : label
      )
    }));
  };

  const removeFriendlyLabel = (index) => {
    setFormData(prev => ({
      ...prev,
      friendlyLabels: prev.friendlyLabels.filter((_, i) => i !== index)
    }));
  };

  const loadDefaultFriendlyLabels = () => {
    if (!businessIndustry) return;
    
    const template = getIndustryTemplate(businessIndustry);
    if (template?.friendlyLabels) {
      setFormData(prev => ({
        ...prev,
        friendlyLabels: template.friendlyLabels,
        setupPrompt: template.setupPrompt || prev.setupPrompt,
        typicalMeasurement: template.typicalEstimate?.toString() || prev.typicalMeasurement,
      }));
    }
  };

  // Calculate preview prices for friendly labels
  const getPreviewPrices = () => {
    const rate = parseFloat(formData.baseRate) || 0;
    const minimum = parseFloat(formData.minimumCharge) || 0;
    
    return formData.friendlyLabels.map(label => {
      const estimate = parseFloat(label.estimate) || 0;
      let price = estimate * rate;
      if (minimum > 0 && price < minimum) {
        price = minimum;
      }
      return {
        ...label,
        calculatedPrice: price,
        isMinimum: minimum > 0 && (estimate * rate) < minimum,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Service name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const isFlat = formData.pricingMethod === 'flat-rate';
      const unit = getUnitForMethod(formData.pricingMethod);
      
      // Prepare modifiers JSON
      const modifiersJson = formData.modifiers.length > 0
        ? JSON.stringify(formData.modifiers.map(m => ({
            name: m.name.trim(),
            description: m.description?.trim() || null,
            type: m.type,
            amount: parseFloat(m.amount) || 0,
            enabled: m.enabled
          })).filter(m => m.name)) // Filter out empty names
        : null;

      // Prepare friendly labels JSON
      const friendlyLabelsJson = formData.friendlyLabels.length > 0
        ? JSON.stringify(formData.friendlyLabels.map(l => ({
            label: l.label.trim(),
            estimate: parseFloat(l.estimate) || 0,
            description: l.description?.trim() || null,
          })).filter(l => l.label)) // Filter out empty labels
        : null;
      
      const payload = {
        id: editingService?.id || 0,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: formData.category.trim() || 'Other',
        unit: unit,
        isActive: formData.isActive,
        // Pricing fields
        defaultPrice: isFlat ? (parseFloat(formData.price) || 0) : (parseFloat(formData.baseRate) || 0),
        pricingTemplate: isFlat ? null : formData.pricingMethod,
        baseRate: isFlat ? null : (parseFloat(formData.baseRate) || null),
        minimumCharge: formData.minimumCharge ? parseFloat(formData.minimumCharge) : null,
        tieredRates: formData.pricingMethod === 'tiered' && formData.tieredRates.length > 0 
          ? formData.tieredRates 
          : null,
        // Modifiers
        modifiersJson: modifiersJson,
        // Friendly Labels (Phase 1)
        friendlyLabelsJson: friendlyLabelsJson,
        useFriendlyLabelsOnPublicForm: formData.useFriendlyLabelsOnPublicForm,
        setupPrompt: formData.setupPrompt?.trim() || null,
        typicalMeasurement: (!isFlat && formData.pricingMethod !== 'tiered' && useSimpleMode && formData.typicalMeasurement) ? parseFloat(formData.typicalMeasurement) : null,
      };

      if (editingService) {
        await api.put(`/servicetypes/${editingService.id}`, payload);
      } else {
        await api.post('/servicetypes', payload);
      }

      await loadServiceTypes();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data || 'Failed to save service type');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm.service) return;

    try {
      await api.delete(`/servicetypes/${deleteConfirm.service.id}`);
      await loadServiceTypes();
      setDeleteConfirm({ show: false, service: null });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete service type');
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || isNaN(amount)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getPricingDisplay = (service) => {
    if (service.pricingTemplate) {
      const method = Object.values(PRICING_METHODS).find(m => m.id === service.pricingTemplate);
      const rate = service.baseRate || service.defaultPrice || 0;
      
      if (service.pricingTemplate === 'tiered') {
        return {
          badge: 'Tiered',
          price: 'Variable pricing',
        };
      }
      
      return {
        badge: method?.name || service.pricingTemplate,
        price: `$${rate < 0.1 ? rate.toFixed(4) : rate.toFixed(2)}/${service.unit?.toLowerCase() || 'unit'}`,
      };
    }
    
    return {
      badge: null,
      price: formatCurrency(service.defaultPrice),
    };
  };

  // Get modifier count for display
  const getModifierCount = (service) => {
    const mods = parseModifiers(service.modifiersJson);
    return mods.filter(m => m.enabled).length;
  };

  // Tiered rate helpers
  const addTieredRate = () => {
    setFormData(prev => ({
      ...prev,
      tieredRates: [...prev.tieredRates, { name: '', description: '', price: '' }]
    }));
  };

  const updateTieredRate = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tieredRates: prev.tieredRates.map((tier, i) => 
        i === index ? { ...tier, [field]: value } : tier
      )
    }));
  };

  const removeTieredRate = (index) => {
    setFormData(prev => ({
      ...prev,
      tieredRates: prev.tieredRates.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="card animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
          <div className="h-10 bg-gray-200 rounded w-32"></div>
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="card animate-pulse">
            <div className="h-5 bg-gray-200 rounded w-1/4 mb-3"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  const isFlat = formData.pricingMethod === 'flat-rate';
  const isTiered = formData.pricingMethod === 'tiered';
  const needsRate = !isFlat && !isTiered;
  const previewPrices = getPreviewPrices();

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="card bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Your Service Types</h2>
            <p className="text-sm text-gray-600 mb-3">
              Configure services with pricing calculators for faster, consistent quotes.
            </p>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                <span className="text-gray-700">
                  <span className="font-semibold">{serviceTypes.length}</span> Total
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-600 rounded-full"></div>
                <span className="text-gray-700">
                  <span className="font-semibold">{serviceTypes.filter(s => s.pricingTemplate).length}</span> With Calculator
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary flex items-center justify-center gap-2 w-full sm:w-auto"
          >
            <Plus size={18} />
            Add Service
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 text-red-500 hover:text-red-700">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Service Types List */}
      {serviceTypes.length === 0 ? (
        <div className="card text-center py-12">
          <Briefcase className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <p className="text-gray-500 text-lg font-medium mb-2">No service types yet</p>
          <p className="text-gray-400 text-sm mb-4">Add your first service to get started</p>
          <button
            onClick={() => handleOpenModal()}
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Plus size={18} />
            Add Service
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(
            serviceTypes.reduce((groups, service) => {
              const category = service.category || 'Other';
              if (!groups[category]) groups[category] = [];
              groups[category].push(service);
              return groups;
            }, {})
          ).map(([category, services]) => (
            <div key={category} className="card p-0 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  {category}
                </h3>
              </div>
              
              <div className="divide-y divide-gray-100">
                {services.map((service) => {
                  const pricing = getPricingDisplay(service);
                  const modifierCount = getModifierCount(service);
                  const hasFriendlyLabels = service.useFriendlyLabelsOnPublicForm && service.friendlyLabelsJson;
                  
                  return (
                    <div
                      key={service.id}
                      className="p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-semibold text-gray-900 truncate">
                              {service.name}
                            </h4>
                            {!service.isActive && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-500 text-xs rounded-full">
                                Inactive
                              </span>
                            )}
                            {pricing.badge && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">
                                <Calculator size={12} />
                                {pricing.badge}
                              </span>
                            )}
                            {modifierCount > 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                                <Plus size={12} />
                                {modifierCount} add-on{modifierCount > 1 ? 's' : ''}
                              </span>
                            )}
                            {hasFriendlyLabels && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                <Sparkles size={12} />
                                Friendly Labels
                              </span>
                            )}
                          </div>
                          {service.description && (
                            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                              {service.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-sm flex-wrap">
                            <span className="font-medium text-primary-600">
                              {pricing.price}
                            </span>
                            {service.minimumCharge && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500">Min: {formatCurrency(service.minimumCharge)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleOpenModal(service)}
                            className="flex-1 sm:flex-none px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Edit2 size={16} />
                            <span className="sm:hidden">Edit</span>
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ show: true, service })}
                            className="flex-1 sm:flex-none px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-1.5"
                          >
                            <Trash2 size={16} />
                            <span className="sm:hidden">Delete</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingService ? 'Edit Service Type' : 'Add Service Type'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-5">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* Service Name */}
              <div>
                <label className="label">Service Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input"
                  placeholder="e.g., Driveway Cleaning"
                />
              </div>

              {/* Description */}
              <div>
                <label className="label">Description <span className="text-gray-400 font-normal">(optional)</span></label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className="input"
                  rows={2}
                  placeholder="Brief description shown on quotes"
                />
              </div>

              {/* Category */}
              <div ref={categoryInputRef} className="relative">
                <label className="label">Category</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  onFocus={() => setShowCategorySuggestions(true)}
                  className="input"
                  placeholder="Type or select..."
                />
                {showCategorySuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {getCategorySuggestions().length > 0 ? (
                      getCategorySuggestions().map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => handleCategorySelect(cat)}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 ${
                            formData.category === cat ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                          }`}
                        >
                          {cat}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        Press Enter to use "{formData.category}"
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* PRICING METHOD */}
              {/* ============================================ */}
              <div className="border-t border-gray-200 pt-5">
                <label className="label flex items-center gap-2">
                  <DollarSign size={16} className="text-primary-600" />
                  Pricing Method
                </label>
                
                <p className="text-sm text-gray-500 mb-3">
                  How do you charge for this service?
                </p>

                {/* Pricing Method Selection */}
                <div className="space-y-2">
                  {/* Flat Rate Option */}
                  <label 
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isFlat 
                        ? 'border-primary-500 bg-primary-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pricingMethod"
                      value="flat-rate"
                      checked={isFlat}
                      onChange={() => handlePricingMethodChange('flat-rate')}
                      className="mt-1 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Flat Rate</div>
                      <div className="text-sm text-gray-500">Same price every time</div>
                      <div className="text-xs text-gray-400 mt-1">e.g., Gutter Cleaning - $150</div>
                    </div>
                  </label>

                  {/* Calculated Option */}
                  <label 
                    className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      !isFlat 
                        ? 'border-purple-500 bg-purple-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="pricingMethod"
                      value="calculated"
                      checked={!isFlat}
                      onChange={() => handlePricingMethodChange('per-sqft')}
                      className="mt-1 h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 flex items-center gap-2">
                        Calculated
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-200 text-purple-700 text-xs rounded">
                          <Calculator size={10} />
                          Calculator
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">Price varies by size, quantity, or time</div>
                      <div className="text-xs text-gray-400 mt-1">Helps you quote consistently & faster</div>
                    </div>
                  </label>
                </div>

                {/* Flat Rate: Simple Price Input */}
                {isFlat && (
                  <div className="mt-4">
                    <label className="label">Price</label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="input pl-8"
                        placeholder="0.00"
                        step="0.0001"
                        min="0"
                      />
                    </div>
                  </div>
                )}

                {/* Calculated: Show calculation type dropdown and rate */}
                {!isFlat && (
                  <div className="mt-4 p-4 bg-purple-50 rounded-lg space-y-4">
                    <div>
                      <label className="label">Calculate By</label>
                      <select
                        value={formData.pricingMethod}
                        onChange={(e) => handlePricingMethodChange(e.target.value)}
                        className="input"
                      >
                        {Object.values(PRICING_METHODS)
                          .filter(m => m.id !== 'flat-rate')
                          .map(method => (
                            <option key={method.id} value={method.id}>
                              {method.name} - {method.description}
                            </option>
                          ))}
                      </select>
                      <p className="text-xs text-purple-600 mt-1">
                        {Object.values(PRICING_METHODS).find(m => m.id === formData.pricingMethod)?.example}
                      </p>
                    </div>

                    {/* ============================================ */}
                    {/* REVERSE CALCULATOR (Simple Mode) */}
                    {/* ============================================ */}
                    {needsRate && (
                      <div className="bg-white rounded-lg p-4 border border-purple-200">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Lightbulb size={16} className="text-amber-500" />
                            <span className="font-medium text-gray-900">
                              {useSimpleMode ? 'Easy Setup' : 'Advanced'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setUseSimpleMode(!useSimpleMode)}
                            className={`text-xs text-purple-600 hover:text-purple-700 items-center gap-1 ${(useSimpleMode || formData.typicalMeasurement) ? 'flex' : 'hidden'}`}
                          >
                            <Settings2 size={12} />
                            {useSimpleMode ? 'Enter exact rate' : 'Use easy setup'}
                          </button>
                        </div>

                        {useSimpleMode ? (
                          /* Simple Mode: Ask typical price, auto-calculate rate */
                          <div className="space-y-3">
                            <div>
                              <label className="label text-sm">
                                {formData.setupPrompt || `What do you charge for a typical job?`}
                              </label>
                              <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                <input
                                  type="number"
                                  name="typicalPrice"
                                  value={formData.typicalPrice}
                                  onChange={handleChange}
                                  className="input pl-8"
                                  placeholder="e.g., 45"
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                            </div>
                            
                            {formData.typicalMeasurement && formData.typicalPrice && (
                              <div className="flex items-center gap-2 text-sm">
                                <Check size={14} className="text-green-600" />
                                <span className="text-green-700">
                                  Calculated rate: <strong>{formatRate(parseFloat(formData.baseRate), getUnitForMethod(formData.pricingMethod).toLowerCase())}</strong>
                                </span>
                              </div>
                            )}

                            {!formData.typicalMeasurement && (
                              <p className="text-xs text-gray-500">
                                Based on a typical measurement of {getIndustryTemplate(businessIndustry)?.typicalEstimate?.toLocaleString() || '?'} {getUnitForMethod(formData.pricingMethod).toLowerCase()}
                              </p>
                            )}
                          </div>
                        ) : (
                          /* Advanced Mode: Direct rate entry */
                          <div>
                            <label className="label text-sm">
                              Rate per {getUnitForMethod(formData.pricingMethod).toLowerCase()}
                            </label>
                            <div className="relative">
                              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                              <input
                                type="number"
                                name="baseRate"
                                value={formData.baseRate}
                                onChange={handleChange}
                                className="input pl-8"
                                placeholder="0.0000"
                                step="0.0001"
                                min="0"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Minimum Charge (for rate-based) */}
                    {needsRate && (
                      <div>
                        <label className="label flex items-center gap-1">
                          Minimum Charge
                          <span className="text-gray-400 font-normal">(optional)</span>
                          <HelpCircle size={14} className="text-gray-400" />
                        </label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                          <input
                            type="number"
                            name="minimumCharge"
                            value={formData.minimumCharge}
                            onChange={handleChange}
                            className="input pl-8"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          If calculated price is below this, charge this amount instead
                        </p>
                      </div>
                    )}

                    {/* Tiered Pricing */}
                    {isTiered && (
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="label mb-0">Price Tiers</label>
                          <button
                            type="button"
                            onClick={addTieredRate}
                            className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                          >
                            + Add Tier
                          </button>
                        </div>
                        
                        {formData.tieredRates.length === 0 ? (
                          <div className="text-center py-4 text-sm text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                            <p>No tiers added yet</p>
                            <button
                              type="button"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  tieredRates: [
                                    { name: 'Small', description: 'Up to 1,500 sq ft', price: '99' },
                                    { name: 'Medium', description: '1,500 - 2,500 sq ft', price: '149' },
                                    { name: 'Large', description: '2,500+ sq ft', price: '199' },
                                  ]
                                }));
                              }}
                              className="text-purple-600 hover:text-purple-700 font-medium mt-2"
                            >
                              Use example tiers
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {formData.tieredRates.map((tier, index) => (
                              <div key={index} className="bg-white rounded-lg p-3 border border-gray-200">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1 grid grid-cols-3 gap-2">
                                    <input
                                      type="text"
                                      value={tier.name}
                                      onChange={(e) => updateTieredRate(index, 'name', e.target.value)}
                                      className="input text-sm"
                                      placeholder="Name"
                                    />
                                    <input
                                      type="text"
                                      value={tier.description}
                                      onChange={(e) => updateTieredRate(index, 'description', e.target.value)}
                                      className="input text-sm"
                                      placeholder="Description"
                                    />
                                    <div className="relative">
                                      <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                      <input
                                        type="number"
                                        value={tier.price}
                                        onChange={(e) => updateTieredRate(index, 'price', e.target.value)}
                                        className="input text-sm pl-6"
                                        placeholder="Price"
                                        step="0.01"
                                      />
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeTieredRate(index)}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ============================================ */}
              {/* FRIENDLY LABELS SECTION (Phase 1) */}
              {/* ============================================ */}
              {needsRate && (
                <div className="border-t border-gray-200 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <label className="label mb-0 flex items-center gap-2">
                      <Sparkles size={16} className="text-blue-600" />
                      Customer-Friendly Options
                      <span className="text-gray-400 font-normal text-xs">(optional)</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowFriendlyLabels(!showFriendlyLabels)}
                      className="text-sm text-blue-600 hover:text-blue-700"
                    >
                      {showFriendlyLabels ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                  </div>
                  
                  <p className="text-sm text-gray-500 mb-3">
                    Show "Small/Medium/Large" options on public forms instead of asking for exact measurements.
                  </p>

                  {showFriendlyLabels && (
                    <div className="bg-blue-50 rounded-lg p-4 space-y-4">
                      {/* Enable toggle */}
                      <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            name="useFriendlyLabelsOnPublicForm"
                            checked={formData.useFriendlyLabelsOnPublicForm}
                            onChange={handleChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                        <span className="text-sm text-gray-700">Show on public quote form</span>
                      </div>

                      {/* Labels editor */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-700">Size Options</label>
                          <div className="flex items-center gap-2">
                            {businessIndustry && (
                              <button
                                type="button"
                                onClick={loadDefaultFriendlyLabels}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                Load defaults
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={addFriendlyLabel}
                              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                            >
                              + Add
                            </button>
                          </div>
                        </div>
                        
                        {formData.friendlyLabels.length === 0 ? (
                          <div className="text-center py-4 text-sm text-gray-500 bg-white rounded-lg border border-dashed border-blue-200">
                            <p>No size options yet</p>
                            <button
                              type="button"
                              onClick={loadDefaultFriendlyLabels}
                              className="text-blue-600 hover:text-blue-700 font-medium mt-2"
                            >
                              Load industry defaults
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {formData.friendlyLabels.map((label, index) => (
                              <div key={index} className="bg-white rounded-lg p-3 border border-blue-200">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1 grid grid-cols-3 gap-2">
                                    <input
                                      type="text"
                                      value={label.label}
                                      onChange={(e) => updateFriendlyLabel(index, 'label', e.target.value)}
                                      className="input text-sm"
                                      placeholder="Label (e.g., Small Yard)"
                                    />
                                    <input
                                      type="number"
                                      value={label.estimate}
                                      onChange={(e) => updateFriendlyLabel(index, 'estimate', e.target.value)}
                                      className="input text-sm"
                                      placeholder={`${getUnitForMethod(formData.pricingMethod)} estimate`}
                                      min="0"
                                    />
                                    <input
                                      type="text"
                                      value={label.description || ''}
                                      onChange={(e) => updateFriendlyLabel(index, 'description', e.target.value)}
                                      className="input text-sm"
                                      placeholder="Description (optional)"
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeFriendlyLabel(index)}
                                    className="p-1 text-red-500 hover:bg-red-50 rounded"
                                  >
                                    <X size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Preview Panel */}
                      {formData.friendlyLabels.length > 0 && formData.baseRate && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                              <Eye size={14} />
                              Customer Preview
                            </label>
                          </div>
                          <div className="bg-white rounded-lg border border-blue-200 p-4">
                            <p className="text-sm text-gray-600 mb-3">What size is your property?</p>
                            <div className="space-y-2">
                              {previewPrices.map((item, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                >
                                  <div>
                                    <span className="font-medium text-gray-900">{item.label}</span>
                                    {item.description && (
                                      <span className="text-gray-500 text-sm ml-2">({item.description})</span>
                                    )}
                                  </div>
                                  <span className="font-semibold text-green-600">
                                    ~{formatCurrency(item.calculatedPrice)}
                                    {item.isMinimum && <span className="text-xs text-gray-400">*</span>}
                                  </span>
                                </div>
                              ))}
                            </div>
                            {previewPrices.some(p => p.isMinimum) && (
                              <p className="text-xs text-gray-500 mt-2">* Minimum charge applied</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ============================================ */}
              {/* ADD-ONS / MODIFIERS SECTION */}
              {/* ============================================ */}
              <div className="border-t border-gray-200 pt-5">
                <div className="flex items-center justify-between mb-3">
                  <label className="label mb-0 flex items-center gap-2">
                    <Plus size={16} className="text-amber-600" />
                    Add-Ons / Modifiers
                    <span className="text-gray-400 font-normal text-xs">(optional)</span>
                  </label>
                  <button
                    type="button"
                    onClick={addModifier}
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium flex items-center gap-1"
                  >
                    <Plus size={14} />
                    Add
                  </button>
                </div>
                
                <p className="text-sm text-gray-500 mb-3">
                  Extra options customers can select (e.g., 2-Story, Heavy Staining)
                </p>

                {formData.modifiers.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500 bg-amber-50 rounded-lg border border-dashed border-amber-200">
                    <p>No add-ons yet</p>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          modifiers: [
                            { name: '2-Story', description: 'Second floor access required', type: 'flat', amount: '100', enabled: true },
                            { name: '3-Story', description: 'Third floor access required', type: 'flat', amount: '200', enabled: true },
                          ]
                        }));
                        setShowModifiers(true);
                      }}
                      className="text-amber-600 hover:text-amber-700 font-medium mt-2"
                    >
                      Add example modifiers
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.modifiers.map((modifier, index) => (
                      <div 
                        key={index} 
                        className={`rounded-lg p-3 border transition-all ${
                          modifier.enabled 
                            ? 'bg-amber-50 border-amber-200' 
                            : 'bg-gray-50 border-gray-200 opacity-60'
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {/* Enable/Disable Toggle */}
                          <button
                            type="button"
                            onClick={() => toggleModifierEnabled(index)}
                            className={`mt-1 flex-shrink-0 ${modifier.enabled ? 'text-amber-600' : 'text-gray-400'}`}
                            title={modifier.enabled ? 'Enabled - click to disable' : 'Disabled - click to enable'}
                          >
                            {modifier.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                          </button>
                          
                          <div className="flex-1 space-y-2">
                            {/* Name and Description Row */}
                            <div className="grid grid-cols-2 gap-2">
                              <input
                                type="text"
                                value={modifier.name}
                                onChange={(e) => updateModifier(index, 'name', e.target.value)}
                                className="input text-sm"
                                placeholder="Name (e.g., 2-Story)"
                              />
                              <input
                                type="text"
                                value={modifier.description || ''}
                                onChange={(e) => updateModifier(index, 'description', e.target.value)}
                                className="input text-sm"
                                placeholder="Description (optional)"
                              />
                            </div>
                            
                            {/* Type and Amount Row */}
                            <div className="grid grid-cols-2 gap-2">
                              <select
                                value={modifier.type}
                                onChange={(e) => updateModifier(index, 'type', e.target.value)}
                                className="input text-sm"
                              >
                                <option value="flat">+ Flat Amount ($)</option>
                                <option value="percent">+ Percentage (%)</option>
                              </select>
                              <div className="relative">
                                {modifier.type === 'flat' ? (
                                  <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                ) : (
                                  <Percent className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                )}
                                <input
                                  type="number"
                                  value={modifier.amount}
                                  onChange={(e) => updateModifier(index, 'amount', e.target.value)}
                                  className="input text-sm pl-7"
                                  placeholder={modifier.type === 'flat' ? '100' : '25'}
                                  step={modifier.type === 'flat' ? '0.01' : '1'}
                                  min="0"
                                />
                              </div>
                            </div>
                          </div>
                          
                          {/* Delete Button */}
                          <button
                            type="button"
                            onClick={() => removeModifier(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Info about modifiers on public form */}
                {formData.modifiers.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2 flex items-start gap-1">
                    <HelpCircle size={12} className="mt-0.5 flex-shrink-0" />
                    Customers will see add-on names as checkboxes. Pricing is hidden - only the final estimate is shown.
                  </p>
                )}
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3 pt-2">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                </label>
                <span className="text-sm text-gray-700">Active</span>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Saving...' : editingService ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, service: null })}
        onConfirm={handleDelete}
        title="Delete Service Type"
        message={`Are you sure you want to delete "${deleteConfirm.service?.name}"? This cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
      />
    </div>
  );
};

export default ServiceTypesTab;